import { ValueSet } from "../types/fhir";
import { apiClientService, ApiGateWayResponse } from "./apiClientService";
import { isNaN } from "mathjs";
import { IExtentionType } from "../types/IQuestionnareItemType";
import { composeFromConcepts, withComposeFromExpansion } from "../helpers/valueSetHelper";

const apiClient = apiClientService.apiGateway;

/**
 * Shape returned by the API gateway's ExpandLatest: a ValueSet's latest version, fully resolved via FHIR $expand.
 */
interface ExpandedConcept {
  system: string;
  version?: string;
  code: string;
  display: string;
}
interface ExpandedValueSet {
  url: string;
  version: string;
  concepts: ExpandedConcept[];
}

/**
 * Adapt an ExpandLatest result into a ValueSet
 */
const toValueSet = (expanded: ExpandedValueSet): ValueSet => {
  return {
    resourceType: "ValueSet",
    status: "active",
    url: expanded.url,
    version: expanded.version,
    compose: composeFromConcepts(expanded.concepts),
  } as ValueSet;
};

const addCanBeCalculatedExtension = (valueSets: Set<ValueSet>) => {
  valueSets.forEach((valueSet) => {
    //Does the valueset contain any include where all codes are numeric?
    if (valueSet.compose?.include?.length ?? 0 > 0) {
      const isNumericCodes = valueSet.compose?.include.some((include) =>
        include.concept?.every((concept) => isNumericOrUN(concept.code))
      );
      valueSet.extension = [
        ...(valueSet.extension ?? []).filter((extension) => extension.url !== IExtentionType.canBeCalculated),
        {
          url: IExtentionType.canBeCalculated,
          valueBoolean: isNumericCodes,
        },
      ];
    }
  });
};

const getAll = async (): Promise<ValueSet[] | undefined> => {
  const response = await apiClient.get<ApiGateWayResponse<ValueSet[]>>(`/api/ValueSet/getAllLatest`);
  if (response.status !== 200) {
    console.error("Failed to fetch valuesets", response.statusText);
    return undefined;
  }
  if (response.data?.error) {
    console.error("Error in response when fetching valuesets", response.data.error.message);
    return undefined;
  }
  if (response.data?.data === undefined) {
    console.error("No data in response when fetching valuesets");
    return undefined;
  }

  const valueSets = response.data.data.map(withComposeFromExpansion);
  addCanBeCalculatedExtension(new Set(valueSets));
  return valueSets;
};
const isNumericOrUN = (num: unknown) => {
  if (num === "UN") {
    return true;
  }
  try {
    return (typeof num === "number" || (typeof num === "string" && num.trim() !== "")) && !isNaN(num as number);
  } catch (e) {
    return false;
  }
};

const canonicalBaseUrl = "https://kip.rkkp.dk/fhir/ValueSet";

export const getByUrl = async (url: string): Promise<ValueSet[] | undefined> => {
  const response = await apiClientService.apiGateway.post<ApiGateWayResponse<ValueSet[]>>(
    "api/ValueSet/GetValueSetsByUrls",
    [url]
  );
  if (response.data?.error) {
    throw new Error(response.data.error.message ?? "An error occurred fetching ValueSet by URL");
  }
  const valueSets = response.data?.data;
  addCanBeCalculatedExtension(new Set(valueSets ?? []));
  return valueSets;
};

export const getByName = async (name: string): Promise<ValueSet[] | undefined> => {
  const canonicalUrl = `${canonicalBaseUrl}/${name}`;
  return getByUrl(canonicalUrl);
};

/**
 * Fetch the latest version of a single ValueSet, server-resolved via $expand
 */
export const expandLatestByUrl = async (url: string): Promise<ValueSet | undefined> => {
  const response = await apiClient.post<ExpandedValueSet[]>("api/ValueSet/ExpandLatest", [url]);
  if (response.status !== 200) {
    console.error("Failed to expand valueset", url, response.statusText);
    return undefined;
  }
  const expanded = response.data?.[0];
  return expanded ? toValueSet(expanded) : undefined;
};

const valuesetService = { getAll, getByUrl, getByName, expandLatestByUrl };

export default valuesetService;
